import { Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useFlags } from "flagsmith/react";
import React from "react";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import * as RemoteData from "src/types/remoteData";
import type { Selected } from "src/types/tags";
import { useBulkActionTargets } from "../forms/components/bulkActionTargetsContext";
import { trackFormAction } from "../forms/components/trackFormAction";
import { useRequireBulkTargets } from "../forms/components/useRequireBulkTargets";
import type { TagBulkSelection, UseTagsReturnType } from "./types";
import { useEnqueueBulkFormTag } from "./useEnqueueBulkFormTag";
import { useTags } from "./useTags";

type UseBulkActionTagsProps = {
  enrollmentPeriodId: uuid;
};

export type UseBulkActionTagsReturnType = UseTagsReturnType & {
  confirmationDialog: React.ReactNode;
  openSelection: () => Promise<void>;
};

export function useBulkActionTags({
  enrollmentPeriodId,
}: UseBulkActionTagsProps): UseBulkActionTagsReturnType {
  const { loadTargets, selectionSize } = useBulkActionTargets();
  const requireTargets = useRequireBulkTargets();
  const enqueueBulkTag = useEnqueueBulkFormTag();
  const queryClient = useQueryClient();
  const isFormServiceEnabled = useFlags(["tags-form-service"])[
    "tags-form-service"
  ].enabled;
  const [bulkSelection, setBulkSelection] =
    React.useState<TagBulkSelection | null>(null);

  const tags = useTags({
    enrollmentPeriodId,
    tagGroupId: null,
    isEditing: true,
    bulkSelection: isFormServiceEnabled ? bulkSelection : null,
  });

  const invalidateQueries = React.useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [FSQ.useFetchTagsForSelectionKey],
    });
    await queryClient.invalidateQueries({
      queryKey: [FSQ.useFetchFormTagsKey],
    });
    await queryClient.invalidateQueries({
      queryKey: [FSQ.useFetchFormSchoolTagsKey],
    });
    await queryClient.invalidateQueries({
      queryKey: [FSQ.useFetchTagsKey],
    });
    await queryClient.invalidateQueries({
      queryKey: [FSQ.useListFormsKey],
    });
  }, [queryClient]);

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: null,
    body: null,
    cancelButton: {
      label: "Cancel",
    },
    confirmButton: {
      label: "Confirm",
      colorScheme: "blue",
    },
    translate: true,
  });

  const onTagUpdate = React.useCallback(
    async (tagId: string, selected: Selected) => {
      const actionType = selected === "All" ? "ADD_TAG" : "REMOVE_TAG";
      trackFormAction({
        verb: "Clicked",
        actionType,
        bulk: true,
        count: selectionSize,
        async: true,
        extra: { tag_id: tagId },
      });

      const tagName =
        tags.tagsProps.tags.toNullable()?.find((tag) => tag.id === tagId)
          ?.name ?? "this tag";
      const formLabel = selectionSize === 1 ? "form" : "forms";

      const confirmed = await confirm(
        selected === "All"
          ? {
              header: (
                <Text>{`Apply "${tagName}" to ${selectionSize} ${formLabel}?`}</Text>
              ),
              body: (
                <Text>
                  This will add the tag to the selected {formLabel}. Updates may
                  take a moment to appear.
                </Text>
              ),
              confirmButton: {
                label: "Yes, apply tag",
                colorScheme: "blue",
              },
            }
          : {
              header: (
                <Text>{`Remove "${tagName}" from ${selectionSize} ${formLabel}?`}</Text>
              ),
              body: (
                <Text>
                  This will remove the tag from the selected {formLabel}.
                  Updates may take a moment to appear.
                </Text>
              ),
              confirmButton: {
                label: "Yes, remove tag",
                colorScheme: "blue",
              },
            }
      );
      if (!confirmed) {
        trackFormAction({
          verb: "Canceled",
          actionType,
          bulk: true,
          count: selectionSize,
          async: true,
          extra: { tag_id: tagId },
        });
        return;
      }

      trackFormAction({
        verb: "Confirmed",
        actionType,
        bulk: true,
        count: selectionSize,
        async: true,
        extra: { tag_id: tagId },
      });

      let queued = false;
      if (selected === "All") {
        queued = await enqueueBulkTag({ actionType: "ADD_TAG", tagId });
      } else if (selected === "None") {
        queued = await enqueueBulkTag({ actionType: "REMOVE_TAG", tagId });
      }

      if (queued) await invalidateQueries();
    },
    [
      confirm,
      enqueueBulkTag,
      invalidateQueries,
      selectionSize,
      tags.tagsProps.tags,
    ]
  );

  const setSelectedRows = tags.setSelectedRows;

  const openSelection = React.useCallback(async () => {
    if (!isFormServiceEnabled) {
      const ids = await loadTargets();
      setSelectedRows(RemoteData.success(ids));
      return;
    }

    setBulkSelection(null);
    const targets = await requireTargets();
    if (!targets) throw new Error("No forms selected");
    setBulkSelection({ targets, selectionSize });
  }, [
    isFormServiceEnabled,
    loadTargets,
    requireTargets,
    selectionSize,
    setSelectedRows,
  ]);

  return {
    ...tags,
    tagsProps: {
      ...tags.tagsProps,
      onTagUpdate,
    },
    confirmationDialog,
    openSelection,
  };
}
